import * as React from 'react';
import CalendarAgendaEventViewController from './CalendarAgendaEventViewController';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { CalendarPaneHeaderTimestrip } from 'owa-calendar-pane-header';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { List } from '@fluentui/react/lib/List';
import { observer } from 'mobx-react-lite';
import {
    endOfDay,
    formatShortWeekDayMonthDay,
    isAfter,
    isBefore,
    isWithinRange,
    OwaDate,
    startOfDay,
} from 'owa-datetime';

import styles from './CalendarAgendaView.scss';

/**
 * Calendar agenda view properties
 */
interface CalendarAgendaViewProps {
    date: OwaDate;
    events: CalendarEvent[];
}

/**
 * Calendar agenda view UI
 */
export default observer(function CalendarAgendaView(props: CalendarAgendaViewProps) {
    const { date, events } = props;
    const agendaEvents = React.useMemo(() => {
        return events.filter(event => isEventValidForAgendaView(event, date));
    }, [events, date]);

    // Set focus on first item on the agenda after its visibility is toggled
    const focusZoneRef = React.useCallback(node => {
        if (node !== null) {
            setTimeout(() => {
                node.focus(true);
            }, 0);
        }
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.agendaViewHeader}>
                <CalendarPaneHeaderTimestrip
                    date={date}
                    textFormatter={formatShortWeekDayMonthDay}
                />
            </div>
            <FocusZone
                ref={focusZoneRef}
                direction={FocusZoneDirection.vertical}
                className={styles.list}>
                <List
                    items={agendaEvents}
                    onRenderCell={onRenderAgendaItem}
                    style={{ padding: '1px' }}
                />
            </FocusZone>
        </div>
    );
});

/**
 * Check if the event is within the range of the agenda view
 */
const isEventValidForAgendaView = (event: CalendarEvent, currentDate: OwaDate): boolean => {
    const startTimeOfDay = startOfDay(currentDate);
    return (
        // events that start on the current date
        isWithinRange(event.Start, startTimeOfDay, endOfDay(currentDate)) ||
        // events that started before the current date and end on the current date or later
        (isBefore(event.Start, startTimeOfDay) && isAfter(event.End, startTimeOfDay))
    );
};

/**
 * Render agenda item
 */
const onRenderAgendaItem = (event): JSX.Element => {
    return <CalendarAgendaEventViewController event={event} />;
};
